import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { Task, TaskStatus } from '../../types/task';
import { Role } from '../../types';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '../ui/form';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { cn } from '../../utils/utils';
import { Checkbox } from '../ui/checkbox';

interface TaskEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Task) => void;
  task: Task | null;
  errors: Record<string, string>;
  users: { id: number; name: string; email: string; role?: string }[];
  currentUserRole?: Role;
}

export const TaskEditDialog: React.FC<TaskEditDialogProps> = ({
  open,
  onClose,
  onSubmit,
  task,
  errors,
  users,
  currentUserRole = Role.CLIENT
}) => {
  const { t } = useTranslation();
  
  // Modifier la définition du formulaire pour inclure la description
  const form = useForm<Task>({
    defaultValues: {
      title: '',
      description: '',
      status: TaskStatus.TODO,
      assignedToId: null,
      dueDate: null
    }
  });
  
  // Reset form when task changes
  useEffect(() => {
    if (task) {
      form.reset({
        id: task.id,
        userId: task.userId,
        title: task.title,
        description: task.description || '',
        status: task.status,
        assignedToId: task.assignedToId,
        dueDate: task.dueDate
      });
    } else {
      form.reset({
        title: '',
        description: '',
        status: TaskStatus.TODO,
        assignedToId: null,
        dueDate: null
      });
    }
  }, [task, form]);
  
  // Vérifier si l'utilisateur peut assigner des tâches
  const canAssignTasks = currentUserRole === 'ADMIN';
  
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>{task ? t('task.edit') : t('task.add')}</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 py-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('task.title')}<span className="text-red-500">*</span></FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  {errors.title && <FormMessage>{errors.title}</FormMessage>}
                </FormItem>
              )}
            />
            
            {/* Ajout du champ pour la description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('task.description')}</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      value={field.value || ''}
                      placeholder={t('task.descriptionPlaceholder', 'Description de la tâche...')}
                      className="resize-y min-h-[100px]"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('task.status')}</FormLabel>
                  <Select 
                    onValueChange={field.onChange}
                    defaultValue={field.value || TaskStatus.TODO}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={TaskStatus.TODO}>{t('task.status.todo')}</SelectItem>
                      <SelectItem value={TaskStatus.IN_PROGRESS}>{t('task.status.in_progress')}</SelectItem>
                      <SelectItem value={TaskStatus.DONE}>{t('task.status.done')}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="assignedToId"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between items-center">
                    <FormLabel>{t('task.assignedTo')}</FormLabel>
                    {!canAssignTasks && (
                      <Badge variant="outline" className="text-xs">
                        {t('task.adminOnly', 'Admin uniquement')}
                      </Badge>
                    )}
                  </div>
                  <Select 
                    onValueChange={(value) => field.onChange(value === 'null' ? null : Number(value))}
                    defaultValue={field.value?.toString() || 'null'}
                    disabled={!canAssignTasks}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('task.unassigned')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="null">{t('task.unassigned')}</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {user.name} {user.role === 'ADMIN' ? '(Admin)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!canAssignTasks && (
                    <FormDescription className="text-xs text-muted-foreground mt-1">
                      {t('task.adminOnlyDesc', 'Seuls les administrateurs peuvent assigner des tâches')}
                    </FormDescription>
                  )}
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="dueDate"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>{t('task.dueDate')}</FormLabel>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="hasDueDate"
                        checked={field.value !== null}
                        onCheckedChange={(checked) => {
                          if (!checked) {
                            field.onChange(null);
                          } else {
                            field.onChange(new Date());
                          }
                        }}
                      />
                      <label
                        htmlFor="hasDueDate"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {t('task.setDueDate')}
                      </label>
                    </div>
                  </div>
                  
                  {field.value && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(new Date(field.value), "PPP")
                            ) : (
                              <span>{t('task.pickDate')}</span>
                            )}
                            <Calendar className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={field.value ? new Date(field.value) : undefined}
                          onSelect={(date) => field.onChange(date ? date.toISOString() : null)}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </FormItem>
              )}
            />
            
            <DialogFooter className="pt-4">
              <Button variant="outline" type="button" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit">
                {task ? t('common.save') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
